import React from "react";
import { motion } from "framer-motion";
import { resolveImg } from "@/lib/api";

const DEFAULT_PARAGRAPHS = [
  "Kate Mercer writes practical, research-driven nonfiction focused on studying, career development and thinking clearly in the age of artificial intelligence.",
  "Drawing on an international career across banking and finance in the United Kingdom, Monaco, Switzerland, Singapore and the United Arab Emirates, Kate has worked within diverse financial institutions and global business environments.",
  "Working across different countries, cultures and industries gave her a unique understanding of how successful people think, learn, adapt and build meaningful careers.",
  "These experiences shaped her belief that lasting success comes through disciplined thinking, continuous learning and practical systems rather than shortcuts or trends.",
  "She is the author of The First Class Student, The Graduate Game: AI Edition and The AI Reflex: Sharpen Your Mind with AI — a growing collection of practical guides for succeeding in an AI-powered world.",
  "Kate writes for students, graduates, professionals and lifelong learners who value evidence over hype, clarity over complexity and advice that can be applied immediately.",
];

const DEFAULT_MISSION =
  "To help people think more clearly, learn more effectively and remain competitive in a world increasingly shaped by artificial intelligence.";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_book-launch-demo/artifacts/vt7vzwyz_18FC9C4C-28EC-4EEC-81B7-ADED393568F5.PNG";

export default function About({ config }) {
  const paragraphs = config?.about_paragraphs?.length ? config.about_paragraphs : DEFAULT_PARAGRAPHS;
  const mission = config?.about_mission || DEFAULT_MISSION;
  const title = config?.about_title || "Meet Kate Mercer";

  return (
    <section id="about" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[0.82fr_1.18fr] gap-16 lg:gap-24 items-start">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative lg:sticky lg:top-28"
        >
          <div className="absolute -inset-4 rounded-[2.25rem] bg-gradient-to-br from-gold/25 to-transparent blur-2xl" />
          <img
            src={config?.author_portrait || PORTRAIT}
            alt="Kate Mercer, author of The First Class Student and The Graduate Game"
            loading="lazy"
            decoding="async"
            data-testid="about-portrait"
            className="relative rounded-[1.9rem] w-full aspect-[4/5] object-cover object-top img-crisp shadow-[0_35px_70px_-25px_rgba(0,0,0,0.4)]"
          />
        </motion.div>

        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">About the Author</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4 mb-9">{title}</h2>
          <div className="space-y-5">
            {paragraphs.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="text-gray-600 leading-relaxed text-[15px] sm:text-base"
              >
                {p}
              </motion.p>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 border-l-2 border-gold pl-6"
          >
            <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-2">Her mission</p>
            <p className="font-display text-xl sm:text-2xl leading-snug text-gray-900 italic">{mission}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
